import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useContext } from 'react';
import ToastContext from '../contexts/ToastContext';
import { supabase } from '../supabase';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const tokenHash = searchParams.get('token_hash');
  const type = searchParams.get('type');
  const emailRef = useRef();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  useEffect(() => {
    if (token || tokenHash) {
      verifyToken();
    }
  }, [token, tokenHash, type]);

  async function verifyToken() {
    setIsVerifying(true);
    setLoading(true);
    try {
      if (!supabase) throw new Error('Supabase is not configured');

      if (tokenHash && type) {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          type,
          token_hash: tokenHash
        });
        if (verifyError) throw verifyError;
      } else if (token) {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          type: 'signup',
          token
        });
        if (verifyError) throw verifyError;
      }

      setSuccess(true);
      if (toast && toast.success) toast.success('Email vérifié avec succès');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      const friendly = err?.message || 'Erreur inconnue';
      setError(friendly);
      if (toast && toast.error) toast.error(friendly);
    }
    setLoading(false);
    setIsVerifying(false);
  }

  async function handleResendSubmit(e) {
    e.preventDefault();
    setError('');
    setResendLoading(true);

    if (!emailRef.current.value) {
      setError('Veuillez entrer votre email');
      setResendLoading(false);
      return;
    }

    try {
      if (!supabase) throw new Error('Supabase is not configured');
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: emailRef.current.value
      });
      if (resendError) throw resendError;
      if (toast && toast.success) toast.success('Email de vérification renvoyé');
    } catch (err) {
      const friendly = err?.message || 'Erreur inconnue';
      setError(friendly);
      if (toast && toast.error) toast.error(friendly);
    }
    setResendLoading(false);
  }

  if (isVerifying) {
    return (
      <div className="container">
        <div className="card" style={{maxWidth:480,margin:'1rem auto', textAlign: 'center'}}>
          <h2>Vérification en cours...</h2>
          <div style={{fontSize: '3rem', margin: '20px 0'}}>⏳</div>
          <p>Veuillez patienter pendant que nous vérifions votre email.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="container">
        <div className="card" style={{maxWidth:480,margin:'1rem auto'}}>
          <h2>Email vérifié</h2>
          <div style={{
            background: 'rgba(76, 175, 80, 0.04)',
            border: '1px solid #4CAF50',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <p style={{color: '#2e7d32', marginBottom: '0', fontSize: '1.1rem'}}>
              ✓ Votre email a été vérifié avec succès!
            </p>
          </div>
          <p style={{textAlign: 'center', color: '#666', marginBottom: '20px'}}>
            Vous allez être redirigé vers la connexion...
          </p>
          <Link to="/login" className="btn btn-primary" style={{width: '100%', textAlign: 'center', textDecoration: 'none'}}>
            Aller à la connexion
          </Link>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="container">
        <div className="card" style={{maxWidth:480,margin:'1rem auto'}}>
          <h2>Vérification d'email</h2>
          <p style={{color: '#666', marginBottom: '20px'}}>
            Entrez votre adresse email pour recevoir un lien de vérification.
          </p>

          {error && (
            <div role="alert" className="card headerless" style={{background:'rgba(239,68,68,0.04)',borderLeft:'4px solid var(--danger)',marginBottom:'12px'}}>
              <div style={{color:'var(--danger)'}}>{error}</div>
            </div>
          )}

          <form onSubmit={handleResendSubmit}>
            <div className="field">
              <label className="muted">Email</label>
              <input 
                className="input" 
                type="email" 
                ref={emailRef} 
                required 
                aria-required="true" 
                placeholder="votre@email.com"
              />
            </div>

            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:20, gap: '10px'}}>
              <Link to="/login" style={{color:'var(--primary)',textDecoration:'none',fontSize:'0.9rem'}}>
                Retour à la connexion
              </Link>
              <button className="btn btn-primary" disabled={resendLoading} type="submit">
                {resendLoading ? 'Envoi...' : 'Renvoyer le lien'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card" style={{maxWidth:480,margin:'1rem auto'}}>
        <h2>Vérification d'email</h2>

        {error && (
          <div role="alert" className="card headerless" style={{background:'rgba(239,68,68,0.04)',borderLeft:'4px solid var(--danger)',marginBottom:'12px'}}>
            <div style={{color:'var(--danger)'}}>{error}</div>
          </div>
        )}

        <div style={{
          background: 'rgba(33, 150, 243, 0.04)',
          border: '1px solid #2196F3',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <p style={{color: '#1565c0', marginBottom: '0'}}>
            ℹ️ Nous vérifions votre email. Si la vérification échoue, vous pouvez renvoyer le lien.
          </p>
        </div>

        <div style={{display: 'flex', gap: '10px', justifyContent: 'center'}}>
          <Link to="/login" className="btn btn-primary" style={{flex: 1, textAlign: 'center', textDecoration: 'none'}}>
            Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
}
